import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getConversationMemory, type ConversationMemoryEntry } from '../services/memory-api.js';

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

@customElement('cp-memory-conversation')
export class CpMemoryConversation extends LitElement {
  createRenderRoot() { return this; }

  @state() private _agentInput = '';
  @state() private _userInput = '';
  @state() private _entries: ConversationMemoryEntry[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _loaded = false;

  private async _load(): Promise<void> {
    const agentId = Number(this._agentInput.trim());
    const userId = Number(this._userInput.trim());
    if (!agentId || !userId) {
      this._error = 'Agent-ID und User-ID (Zahlen) erforderlich';
      return;
    }
    this._loading = true;
    this._error = '';
    try {
      const res = await getConversationMemory(agentId, userId);
      this._entries = res.entries;
      this._loaded = true;
    } catch (e) {
      this._error = (e as Error).message;
    } finally {
      this._loading = false;
    }
  }

  render() {
    return html`
      <div class="cp-memory-conversation">
        <div class="cp-memory-conversation__header">
          <h4 class="cp-memory-conversation__title">Konversations-Gedächtnis</h4>
        </div>
        <div class="cp-memory-conversation__controls">
          <input
            class="cp-memory-conversation__input"
            type="number"
            placeholder="Agent-ID"
            .value=${this._agentInput}
            @input=${(e: Event) => { this._agentInput = (e.target as HTMLInputElement).value; }}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this._load(); }}
          />
          <input
            class="cp-memory-conversation__input"
            type="number"
            placeholder="User-ID"
            .value=${this._userInput}
            @input=${(e: Event) => { this._userInput = (e.target as HTMLInputElement).value; }}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this._load(); }}
          />
          <button class="cp-memory-conversation__btn" @click=${() => this._load()}>Laden</button>
        </div>

        ${this._error
          ? html`<div class="cp-memory-conversation__error">${this._error}</div>`
          : nothing}

        ${this._loading
          ? html`<div class="cp-memory-conversation__loading">Laedt…</div>`
          : this._loaded ? this._renderEntries() : nothing}
      </div>
    `;
  }

  private _renderEntries() {
    if (this._entries.length === 0) {
      return html`<div class="cp-memory-conversation__empty">Kein Konversations-Gedächtnis für diese Kombination.</div>`;
    }
    return html`
      <div class="cp-memory-conversation__list">
        ${this._entries.map(e => html`
          <div class="cp-memory-conversation__entry">
            <div class="cp-memory-conversation__entry-meta">
              <span class="cp-memory-conversation__msgs">${e.message_count} Nachrichten</span>
              ${e.tokens_estimate ? html`<span class="cp-memory-conversation__tokens">~${e.tokens_estimate} Tokens</span>` : nothing}
              <span class="cp-memory-conversation__updated">${fmtDateTime(e.last_activity_at)}</span>
            </div>
            <div class="cp-memory-conversation__summary">${e.summary}</div>
          </div>
        `)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-memory-conversation': CpMemoryConversation;
  }
}
